import { Loader2, RefreshCw, ArrowLeftRight, ShoppingCart } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { Card, CardContent } from '@/components/ui/card';
import { ChartCardHeader } from '@/components/metrics/chart-card-header';
import { formatNumber } from '@/components/metrics/formatters';
import {
  OrdersCompositionDonut,
  FIRST_PURCHASE_COLOR,
  IN_APP_REPURCHASE_COLOR,
  CHANNEL_MIGRATION_COLOR,
} from '@/components/orders-composition-donut';
import { MenuTag } from '../menu-tag';
import type { RepurchaseSegment } from '@/lib/orders-drilldown';

interface OrdersCompositionCardProps {
  firstPurchaseCount: number;
  firstPurchaseRevenue: number;
  inAppRepurchaseCount: number;
  inAppRepurchaseRevenue: number;
  channelMigrationCount: number;
  channelMigrationRevenue: number;
  currency: string;
  isLoading: boolean;
  /** When provided, the donut, its center and the legend rows are clickable. */
  onSegmentClick?: (segment: RepurchaseSegment) => void;
}

export function OrdersCompositionCard({
  firstPurchaseCount,
  firstPurchaseRevenue,
  inAppRepurchaseCount,
  inAppRepurchaseRevenue,
  channelMigrationCount,
  channelMigrationRevenue,
  currency,
  isLoading,
  onSegmentClick,
}: OrdersCompositionCardProps) {
  const { LL, locale } = useI18nContext();

  const totalRepurchases = inAppRepurchaseCount + channelMigrationCount;
  const totalOrders =
    firstPurchaseCount + inAppRepurchaseCount + channelMigrationCount;
  const repurchasePercentage =
    totalOrders > 0 ? Math.round((totalRepurchases / totalOrders) * 100) : null;

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n);

  const legendItems: Array<{
    key: string;
    segment: RepurchaseSegment;
    color: string;
    icon: LucideIcon;
    label: string;
    description: string;
    count: number;
    revenue: number;
  }> = [
    {
      key: 'inAppRepurchase',
      segment: 'in_app',
      color: IN_APP_REPURCHASE_COLOR,
      icon: RefreshCw,
      label: LL.metricsPage.ordersComposition.inAppRepurchase.label(),
      description:
        LL.metricsPage.ordersComposition.inAppRepurchase.description(),
      count: inAppRepurchaseCount,
      revenue: inAppRepurchaseRevenue,
    },
    {
      key: 'channelMigration',
      segment: 'channel_migration',
      color: CHANNEL_MIGRATION_COLOR,
      icon: ArrowLeftRight,
      label: LL.metricsPage.ordersComposition.channelMigration.label(),
      description:
        LL.metricsPage.ordersComposition.channelMigration.description(),
      count: channelMigrationCount,
      revenue: channelMigrationRevenue,
    },
    {
      key: 'firstPurchase',
      segment: 'first_purchase',
      color: FIRST_PURCHASE_COLOR,
      icon: ShoppingCart,
      label: LL.metricsPage.ordersComposition.firstPurchase.label(),
      description: LL.metricsPage.ordersComposition.firstPurchase.description(),
      count: firstPurchaseCount,
      revenue: firstPurchaseRevenue,
    },
  ];

  const centerLabels = (
    <>
      <span className="text-2xl font-bold leading-none">
        {repurchasePercentage != null ? `${repurchasePercentage}%` : '--'}
      </span>
      <span className="mt-1 text-xs text-muted-foreground">
        {LL.metricsPage.ordersComposition.repurchasesCenterLabel()}
      </span>
    </>
  );
  const centerContent =
    onSegmentClick && totalOrders > 0 ? (
      <button
        type="button"
        className="pointer-events-auto flex size-[120px] cursor-pointer flex-col items-center justify-center rounded-full hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={LL.metricsPage.repurchaseDrilldown.viewOrdersAriaLabel({
          label: LL.metricsPage.repurchases.title(),
        })}
        onClick={() => onSegmentClick('total_repurchase')}
      >
        {centerLabels}
      </button>
    ) : (
      centerLabels
    );

  return (
    <Card>
      <ChartCardHeader
        title={LL.metricsPage.ordersComposition.title()}
        helpKey="ordersComposition"
        rightContent={<MenuTag text={LL.common.beta()} color="bg-blue-500" />}
      />
      <CardContent>
        {isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="flex w-full flex-wrap items-start justify-center gap-4">
            <div className="relative size-[200px] shrink-0">
              <OrdersCompositionDonut
                firstPurchaseCount={firstPurchaseCount}
                inAppRepurchaseCount={inAppRepurchaseCount}
                channelMigrationCount={channelMigrationCount}
                onSegmentClick={onSegmentClick}
              />
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                {centerContent}
              </div>
            </div>

            <div className="flex min-w-[260px] flex-1 basis-[min(100%,260px)] flex-col gap-2">
              {legendItems.map((item, index) => {
                const Icon = item.icon;
                const isFirst = index === 0;
                const rowClassName = isFirst
                  ? 'flex flex-col gap-1'
                  : 'flex flex-col gap-1 border-t border-border pt-2';
                const rowContent = (
                  <>
                    <div className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1">
                      <Icon
                        className="mt-0.5 size-4 shrink-0"
                        style={{ color: item.color }}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 text-sm font-semibold text-foreground break-words">
                        {item.label}
                      </span>
                      {totalOrders > 0 ? (
                        <span className="text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {formatNumber(item.count, locale)}
                            <span className="font-normal text-xs text-muted-foreground">
                              {' '}
                              ({formatCurrency(item.revenue)})
                            </span>
                          </span>
                        </span>
                      ) : null}
                    </div>
                    <p className="pl-6 text-xs text-muted-foreground break-words">
                      {item.description}
                    </p>
                  </>
                );
                return onSegmentClick && totalOrders > 0 ? (
                  <button
                    key={item.key}
                    type="button"
                    className={`${rowClassName} w-full cursor-pointer rounded-md text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
                    aria-label={LL.metricsPage.repurchaseDrilldown.viewOrdersAriaLabel(
                      { label: item.label }
                    )}
                    onClick={() => onSegmentClick(item.segment)}
                  >
                    {rowContent}
                  </button>
                ) : (
                  <div key={item.key} className={rowClassName}>
                    {rowContent}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
